import { Home } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ProfilePopover } from './popover/ProfilePopover'
import { NotificationsPopover } from './popover/NotificationsPopover'
import { DepositFundsPopover } from './popover/DepositFundsPopover'
import { SwapPopover } from './popover/SwapPopover'
import { AddWhitelistedAddressPopover } from './popover/AddWhitelistedAddressPopover'
import { useNavigation } from '../contexts/NavigationContext'
import { cn } from '@valiant/ui-shared'

interface FloatingActionBarProps {
  notificationsOpen: boolean
  onNotificationsChange: (open: boolean) => void
  depositFundsOpen: boolean
  onDepositFundsChange: (open: boolean) => void
  swapOpen: boolean
  onSwapChange: (open: boolean) => void
  addWhitelistedAddressOpen: boolean
  onAddWhitelistedAddressChange: (open: boolean) => void
  profileOpen: boolean
  onProfileChange: (open: boolean) => void
}

export function FloatingActionBar({
  notificationsOpen,
  onNotificationsChange,
  depositFundsOpen,
  onDepositFundsChange,
  swapOpen,
  onSwapChange,
  addWhitelistedAddressOpen,
  onAddWhitelistedAddressChange,
  profileOpen,
  onProfileChange,
}: FloatingActionBarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { isSwapMode } = useNavigation()

  // Check if we're on the home page
  const isHomePage = location.pathname === '/'

  // Check if we're on mobile pages
  const isOnMobilePage = location.pathname.startsWith('/mobile/')

  // Check specific mobile routes
  const isOnMobileProfile = location.pathname === '/mobile/profile'
  const isOnMobileNotifications = location.pathname === '/mobile/notifications'
  const isOnMobileDepositFunds = location.pathname === '/mobile/deposit-funds'
  const isOnMobileSwap = location.pathname === '/mobile/swap'
  const isOnMobileAddWhitelistedAddress = location.pathname === '/mobile/add-whitelisted-address'

  // Check if any popover is open on desktop (for future use if needed)
  // const isAnyPopoverOpen = notificationsOpen || depositFundsOpen || swapOpen || profileOpen

  // On mobile, home button should only be active if we're on home page AND no mobile page is active
  const isHomeButtonActive = isHomePage && !isOnMobilePage

  // Mobile button active states - active when on corresponding mobile route
  const isProfileButtonActive = isOnMobileProfile
  const isNotificationsButtonActive = isOnMobileNotifications
  const isDepositFundsButtonActive = isOnMobileDepositFunds
  const isSwapButtonActive = isOnMobileSwap
  const isAddWhitelistedAddressButtonActive = isOnMobileAddWhitelistedAddress

  // Mobile navigation handlers
  const handleMobileProfileClick = () => {
    navigate('/mobile/profile')
  }

  const handleMobileNotificationsClick = () => {
    navigate('/mobile/notifications')
  }

  const handleMobileDepositFundsClick = () => {
    navigate('/mobile/deposit-funds')
  }

  const handleMobileSwapClick = () => {
    navigate('/mobile/swap')
  }

  const handleMobileAddWhitelistedAddressClick = () => {
    navigate('/mobile/add-whitelisted-address')
  }

  return (
    <div className="fixed bottom-0 w-full md:w-auto md:right-6 md:top-1/2 md:transform md:-translate-y-1/2 z-[9999]">
      <div className="bg-white rounded-t-lg overflow-hidden md:overflow-auto w-full md:w-auto md:rounded-full md:p-3 shadow-lg md:border md:border-gray-200 flex flex-row justify-around md:flex-col md:justify-start md:gap-3 h-16 md:h-auto">
        <button
          className={cn(
            'cursor-pointer w-full md:w-16 md:h-16 md:rounded-full md:hadow-sm md:border md:border-gray-200 flex items-center justify-center transition-colors',
            isHomeButtonActive ? 'bg-main text-white hover:bg-main' : 'hover:bg-gray-50',
          )}
          onClick={() => navigate('/')}
        >
          <Home className="w-8 h-8" />
        </button>

        {isSwapMode ? (
          <>
            <DepositFundsPopover
              open={depositFundsOpen}
              onOpenChange={onDepositFundsChange}
              onMobileClick={handleMobileDepositFundsClick}
              isActive={isDepositFundsButtonActive}
            />
            <SwapPopover
              open={swapOpen}
              onOpenChange={onSwapChange}
              onMobileClick={handleMobileSwapClick}
              isActive={isSwapButtonActive}
            />
            <AddWhitelistedAddressPopover
              open={addWhitelistedAddressOpen}
              onOpenChange={onAddWhitelistedAddressChange}
              onMobileClick={handleMobileAddWhitelistedAddressClick}
              isActive={isAddWhitelistedAddressButtonActive}
            />
          </>
        ) : (
          <NotificationsPopover
            open={notificationsOpen}
            onOpenChange={onNotificationsChange}
            onMobileClick={handleMobileNotificationsClick}
            isActive={isNotificationsButtonActive}
          />
        )}

        <ProfilePopover
          open={profileOpen}
          onOpenChange={onProfileChange}
          onMobileClick={handleMobileProfileClick}
          isActive={isProfileButtonActive}
        />
      </div>
    </div>
  )
}
